import styled from '@emotion/styled';
import type { ImageProps, StaticImageData } from 'next/image';
import Image from 'next/image';
import type { ButtonHTMLAttributes, Ref } from 'react';
import { useState, forwardRef, useId } from 'react';

interface ImageButtonProps extends Omit<ImageProps, 'onClick'> {
  src: string | StaticImageData;
  mouseOverSrc?: string | StaticImageData;
  'data-testid'?: string;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
}

const ImageButtonDiv = styled.button`
display: flex;
align-items: center;
justify-content: center;
border: none;
background: none;
cursor: pointer;
`;

const ImageShowControlDiv = styled.div<{ show: boolean }>`
display: ${({ show }) => show ? 'block' : 'none'};
`;

const ImageButton = (
  {
    className,
    onClick,
    'data-testid': dataTestId,
    src,
    mouseOverSrc,
    type = 'button',
    ...imageProps
  }: ImageButtonProps,
  forwardedRef: Ref<HTMLButtonElement>,
) => {
  const imageButtonId = useId();
  const [mouseOver, setMouseOver] = useState(false);

  return (
    <ImageButtonDiv
      ref={forwardedRef}
      id={imageButtonId}
      className={className}
      onClick={onClick}
      onMouseOver={() => setMouseOver(true)}
      onMouseOut={() => setMouseOver(false)}
      type={type}
      data-testid={dataTestId}
    >
      {!mouseOverSrc && (
        <Image
          src={src}
          {...imageProps}
        />
      )}
      {mouseOverSrc && (
        <>
          <ImageShowControlDiv
            show={!mouseOver}
          >
            <Image
              src={src}
              {...imageProps}
            />
          </ImageShowControlDiv>
          <ImageShowControlDiv
            show={mouseOver}
          >
            <Image
              src={mouseOverSrc}
              {...imageProps}
            />
          </ImageShowControlDiv>
        </>
      )}
    </ImageButtonDiv>
  );
};

export default forwardRef(ImageButton);
